// The targets steps of "Add pages from a PDF" (Lesson brief only): the vocabulary and grammar targets the AI reads from
// the pages' text as the teacher checked it (service/verbaschola_pdf_targets.py), which the teacher reviews beside the
// pages. Those chosen become entries of the Description page's target lists (component/targetEntries.ts).
// A list receiving new targets becomes "I'll provide"; an empty or skipped list keeps its mode.
import type { IVerbaScholaPdfTargets, PdfTargetEvidence } from '../../api/VerbaScholaApi';
import { instructionalPinyin } from '../studentInstructionalPinyin';

export interface VocabularyRow extends PdfTargetEvidence {
    key: number;
    selected: boolean;
    hanzi: string;
    pinyin: string;
    meaning: string;
    /** The page it is shown with. */
    pageNumber: number;
    /** Every page the AI read it from (one target read on two batches' pages is one row); a teacher's row, its page. */
    pageNumbers: number[];
    /** The pages known to list it (a listed target's; others none): when only pages using it stay chosen, it is used there, not listed. */
    listedPages?: number[];
    /** Which of its pages lists it is not known (several hold its entry): with one of them left out, the teacher checks it. */
    listedUnsure?: boolean;
    /** The teacher ticked or unticked it: defaults (see withDefaults) leave it as it is. */
    chosenByTeacher?: boolean;
    /** The word as the AI found it (the teacher may correct it since): the same word found again is this row (see isFoundWord). */
    foundAs?: { hanzi: string; pinyin: string };
    /** The pinyin (or meaning) is not in the page's entry as the AI quoted it: the AI supplied or changed it, or quoted too little. */
    pinyinFromAi: boolean;
    meaningFromAi: boolean;
    note: string | null;
    /** Why it is not chosen at first: already one of the lesson's targets (or with another reading), or in the box. */
    existing: string | null;
}

export interface GrammarRow extends PdfTargetEvidence {
    key: number;
    selected: boolean;
    pattern: string;
    explanation: string;
    example: string;
    pageNumber: number;
    pageNumbers: number[];
    /** The pages known to list it (a listed target's; others none): when only pages using it stay chosen, it is used there, not listed. */
    listedPages?: number[];
    /** Which of its pages lists it is not known (several hold its entry): with one of them left out, the teacher checks it. */
    listedUnsure?: boolean;
    /** The teacher ticked or unticked it: defaults (see withDefaults) leave it as it is. */
    chosenByTeacher?: boolean;
    /** The pattern as the AI found it (the teacher may correct it since): the same point found again is this row (see isFoundPoint). */
    foundAs?: string;
    /** The explanation is not on the page as written: the AI wrote (or reworded) it. */
    explanationFromAi: boolean;
    /** The pattern has no Chinese to find in the page's words naming it, so it could not be checked. */
    patternUnchecked: boolean;
    /** Its example is printed on the page, but that it shows this point is the AI's reading (the pattern has no Chinese). */
    exampleUnchecked: boolean;
    note: string | null;
    existing: string | null;
}

/** The lesson's targets already there: the entries of its target lists, as edited. (Text typed in a target box is not a
 * target: the lists become I'll provide, which does not read it.) */
export interface ExistingTargets {
    vocabulary: { hanzi: string; pinyin?: string[] | null }[];
    grammar: string[];
}

/** A warning of the Check text step still open on its page (its reading still in the text, or its line as read), shown
 * beside the targets on its line. */
export interface SourceWarning { pageNumber: number; line: string; reading: string | null; why: string; suggestion: string | null }

/** The targets chosen, as the teacher left them, for the target lists. */
export interface PdfChosenTargets {
    vocabulary: { hanzi: string; pinyin: string; meaning: string }[];
    grammar: { pattern: string; explanation: string; example: string }[];
}

const HAN_RUN = /[㐀-鿿豈-﫿]+/g;
const toned =(pinyin: string) => pinyin.normalize('NFC').toLowerCase().replace(/[\s'’-]/g, '');

/** A pattern written alike apart from formatting: width, spacing, case, and the length of its ellipses (从…到…, 从……到……).
 * Its words and placeholders all count: "Verb + 了 + Object" and "Sentence + 了" are two points (as the server compares). */
export function samePoint(a: string, b: string): boolean {
    const key = (text: string) => text.normalize('NFKC').replace(/\s+/g, '').toLowerCase().replace(/\.{2,}/g, '…');
    return key(a) === key(b);
}

/** A word, pattern or meaning on one line: its line breaks and runs of spaces as one space (its box is one line). */
function oneLine(part: string): string {
    return part.replace(/\s+/g, ' ').trim();
}

/** A target as found, with every page it was read from when the same target was read on several batches' pages. */
type Found<T> = T & { pages?: number[]; listedPages?: number[]; listedUnsure?: boolean };

/** The words found, as rows to review; one the lesson already has (or has with another reading) starts unchosen, said so. */
export function vocabularyRows(found: Found<IVerbaScholaPdfTargets['vocabulary'][number]>[], existing: ExistingTargets, nextKey: () => number): VocabularyRow[] {
    return found.map(item => {
        const saved = existing.vocabulary.find(target => target.hanzi === item.hanzi);
        const savedReading = saved ? instructionalPinyin(saved.pinyin) : '';
        const conflict = Boolean(saved && item.pinyin && savedReading && toned(savedReading) !== toned(item.pinyin));
        const note = saved
            ? conflict ? `This lesson already has ${item.hanzi} as a target, read ${savedReading}. Adding this one does not change it: to use this reading, edit that target on the Description page.` : "Already one of this lesson's targets."
            : null;
        return {
            ...item, key: nextKey(), selected: !note, hanzi: item.hanzi, pinyin: item.pinyin ?? '', meaning: item.meaning ?? '', pageNumber: item.page_number,
            pageNumbers: item.pages ?? item.page_numbers ?? [item.page_number], foundAs: { hanzi: item.hanzi, pinyin: item.pinyin ?? '' },
            pinyinFromAi: Boolean(item.pinyin) && !item.pinyin_printed, meaningFromAi: Boolean(item.meaning) && !item.meaning_printed,
            note: item.note, existing: note,
        };
    });
}

/** The grammar points found, as rows to review; one the lesson already has starts unchosen, said so. */
export function grammarRows(found: Found<IVerbaScholaPdfTargets['grammar'][number]>[], existing: ExistingTargets, nextKey: () => number): GrammarRow[] {
    return found.map(item => {
        const note = existing.grammar.some(pattern => samePoint(pattern, item.pattern)) ? "Already one of this lesson's targets." : null;
        return {
            ...item, key: nextKey(), selected: !note, pattern: item.pattern, explanation: item.explanation ?? '', example: item.example ?? '',
            pageNumber: item.page_number, pageNumbers: item.pages ?? item.page_numbers ?? [item.page_number], foundAs: item.pattern, explanationFromAi: Boolean(item.explanation) && !item.explanation_printed, patternUnchecked: !item.pattern_checked,
            exampleUnchecked: Boolean(item.example) && !item.example_checked,
            note: item.note, existing: note,
        };
    });
}

/** Words and grammar points chosen more than once: the lesson holds each once, so the teacher keeps one. */
export function chosenTwice(vocabulary: VocabularyRow[], grammar: GrammarRow[]): string[] {
    const words = vocabulary.filter(row => row.selected && row.hanzi.trim()).map(row => oneLine(row.hanzi));
    const points = grammar.filter(row => row.selected && row.pattern.trim()).map(row => oneLine(row.pattern));
    return [...words.filter((word, index) => words.indexOf(word) !== index),
        ...points.filter((point, index) => points.findIndex(other => samePoint(other, point)) !== index)]
        .filter((item, index, all) => all.indexOf(item) === index);
}

/** A word's key, to read the same word once when pages are read in batches. */
export function sameWord(a: { hanzi: string; pinyin: string }, b: { hanzi: string; pinyin: string }): boolean {
    return a.hanzi === b.hanzi && toned(a.pinyin) === toned(b.pinyin);
}

/** Whether a row is this word, found again: as the AI first found it, or as it is now (the teacher's correction, or a
 * row of the teacher's own). A row the teacher corrected is not added again when its pages are read again. */
export function isFoundWord(row: VocabularyRow, word: { hanzi: string; pinyin: string }): boolean {
    return sameWord(row, word) || Boolean(row.foundAs && sameWord(row.foundAs, word));
}

/** Whether a row is this grammar point, found again (see isFoundWord). */
export function isFoundPoint(row: GrammarRow, pattern: string): boolean {
    return samePoint(row.pattern, pattern) || Boolean(row.foundAs && samePoint(row.foundAs, pattern));
}

/** Whether the rows hold the characters, as found or as they are now: a suggestion of them is not added again. */
export function hasHanzi(rows: VocabularyRow[], hanzi: string): boolean {
    return rows.some(row => row.hanzi === hanzi || row.foundAs?.hanzi === hanzi);
}

/** The rows chosen (with their characters or pattern), as the teacher left them; an explanation keeps its lines. */
export function chosenTargets(vocabulary: VocabularyRow[], grammar: GrammarRow[]): PdfChosenTargets {
    return {
        vocabulary: vocabulary.filter(row => row.selected && row.hanzi.trim())
            .map(row => ({ hanzi: oneLine(row.hanzi), pinyin: oneLine(row.pinyin), meaning: oneLine(row.meaning) })),
        grammar: grammar.filter(row => row.selected && row.pattern.trim())
            .map(row => ({ pattern: oneLine(row.pattern), explanation: row.explanation.trim(), example: oneLine(row.example) })),
    };
}

/** The open warnings on a target's lines: on any page it was read from, on a line holding its word (a grammar point: its
 * pattern's Chinese parts in order, or its example). What the Check text step flagged is shown with the target itself,
 * not left to the AI's notes. */
export function warningsFor(row: VocabularyRow | GrammarRow, warnings: SourceWarning[]): SourceWarning[] {
    return warnings.filter(warning => row.pageNumbers.includes(warning.pageNumber) && onLine(row, warning.line));
}

/** Whether a line holds the target: its word; a grammar point, its pattern's Chinese parts in order, or its example. */
function onLine(row: VocabularyRow | GrammarRow, line: string): boolean {
    if ('hanzi' in row) {
        const word = row.hanzi.trim();
        return Boolean(word) && line.includes(word) || Boolean(row.translated && row.source_term && line.includes(row.source_term));
    }
    const parts = row.pattern.match(HAN_RUN) ?? [];
    const example = row.example.trim();
    let at = 0;
    const inOrder = parts.length > 0 && parts.every(part => { const found = line.indexOf(part, at); at = found + part.length; return found >= 0; });
    return inOrder || Boolean(example && line.includes(example));
}

/**
 * Whether a page's text holds the target on one of its lines (see onLine): only a sign that it may be the same target
 * there (行 is in 银行 too), for the teacher to check. A point with no Chinese and no example cannot be found.
 */
export function onPageText(row: VocabularyRow | GrammarRow, text: string): boolean {
    return text.split('\n').some(line => onLine(row, line));
}

/**
 * Targets found, one per target, each with every page it was read from: the pages the server found holding it in one
 * reading (page_numbers), and those of the same target read on other batches' pages; and, of one the pages list, the
 * pages listing it (listed_page_numbers), so too.
 */
export function mergeFound<T extends { page_number: number; page_numbers?: number[]; listed_page_numbers?: number[]; listed_pages_unsure?: boolean }>(
    found: T[], same: (a: T, b: T) => boolean): Found<T>[] {
    const union = (a: number[], b: number[]) => [...new Set([...a, ...b])].sort((x, y) => x - y);
    const merged: Found<T>[] = [];
    for (const item of found) {
        const pages = item.page_numbers ?? [item.page_number];
        const listed = item.listed_page_numbers;
        const first = merged.find(other => same(other, item));
        if (!first) merged.push({ ...item, pages: [...pages], ...(listed ? { listedPages: [...listed], listedUnsure: Boolean(item.listed_pages_unsure) } : {}) });
        else {
            first.pages = union(first.pages ?? [], pages);
            if (listed) {
                first.listedPages = union(first.listedPages ?? [], listed);
                first.listedUnsure = Boolean(first.listedUnsure || item.listed_pages_unsure);
            }
        }
    }
    return merged;
}

/** The groups a target's row is shown in, in this order: listed on the pages, used in the reading, suggested by the AI,
 * added by the teacher. */
export const TARGET_GROUPS = ['listed', 'reading', 'suggested', 'teacher'] as const;
export type TargetOrigin = typeof TARGET_GROUPS[number];

/** A row's group: one read without a given origin is from the pages' own lists. */
export function originOf(row: VocabularyRow | GrammarRow): TargetOrigin {
    return row.origin ?? 'listed';
}

/** The rows in their groups' order (each group as it was), so the rows' numbers follow the order they are shown in. */
export function orderedRows<Row extends VocabularyRow | GrammarRow>(rows: Row[]): Row[] {
    return TARGET_GROUPS.flatMap(group => rows.filter(row => originOf(row) === group));
}

/** How many of the rows are chosen targets: ticked, with characters (or a pattern), as they are added (a row the
 * teacher has not filled in yet is no target, to count, to suggest around, or to add). */
export function chosenCount(rows: (VocabularyRow | GrammarRow)[]): number {
    return rows.filter(row => row.selected && ('hanzi' in row ? row.hanzi : row.pattern).trim()).length;
}

/**
 * The terms the reading uses start chosen only in a list the pages printed none of (a general reading's), read
 * without failing; where the pages list targets, or their list could not be read, they start unchosen. Applied after
 * every reading, so a retry that finds a list (or none) sets them again, except those the teacher ticked or unticked.
 */
export function withDefaults<Row extends VocabularyRow | GrammarRow>(rows: Row[], generalReading: boolean): Row[] {
    return rows.map(row => (originOf(row) === 'reading' && !row.chosenByTeacher ? { ...row, selected: generalReading && !row.existing } : row));
}
